// src/auth/refresh.ts
//
// idToken は約1時間で失効する。各 fetch は sessionStorage の "id_token" を
// 直接読むため、失効前にバックグラウンドで refresh_token を使って更新し、
// sessionStorage を常に新鮮に保つ。これにより既存の呼び出し側は無改修で済む。
import { getAppConfig } from "./appConfig";

// id_token(JWT) の exp(秒) を取り出す。取れなければ null。
function decodeExp(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    );
    return typeof json.exp === "number" ? json.exp : null;
  } catch {
    return null;
  }
}

let inflight: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const refreshToken = sessionStorage.getItem("refresh_token");
  if (!refreshToken) return false;

  let region: string;
  let clientId: string;
  try {
    const cfg = getAppConfig();
    region = cfg.COGNITO_REGION;
    clientId = cfg.COGNITO_CLIENT_ID;
  } catch {
    return false; // config 未ロード
  }

  try {
    const res = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
      },
      body: JSON.stringify({
        AuthFlow: "REFRESH_TOKEN_AUTH",
        ClientId: clientId,
        AuthParameters: { REFRESH_TOKEN: refreshToken },
      }),
    });

    const data = await res.json();
    if (!res.ok || data.__type) return false;

    const result = data.AuthenticationResult;
    if (!result?.IdToken) return false;

    sessionStorage.setItem("id_token", result.IdToken);
    if (result.AccessToken) {
      sessionStorage.setItem("access_token", result.AccessToken);
    }
    // REFRESH_TOKEN_AUTH は新しい refresh_token を返さないため既存を維持する。
    window.dispatchEvent(new Event("exhibi:session-updated"));
    return true;
  } catch {
    return false;
  }
}

/** refresh_token で idToken を更新する。並行呼び出しは1回にまとめる。 */
export function refreshSession(): Promise<boolean> {
  if (!inflight) {
    inflight = doRefresh().finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

const MARGIN_SEC = 5 * 60; // 失効の5分前から更新

async function maybeRefresh(): Promise<void> {
  const idToken = sessionStorage.getItem("id_token");
  const refreshToken = sessionStorage.getItem("refresh_token");
  if (!idToken || !refreshToken) return; // 未ログイン or 旧セッション

  const exp = decodeExp(idToken);
  const now = Math.floor(Date.now() / 1000);
  if (exp === null || exp - now <= MARGIN_SEC) {
    await refreshSession();
  }
}

let started = false;

/** アプリ起動時に1回だけ呼ぶ。失効前の自動更新を仕掛ける。 */
export function startTokenAutoRefresh(): void {
  if (started) return;
  started = true;

  // 起動直後に一度チェック（PC スリープ復帰やリロード直後の失効に対応）
  void maybeRefresh();

  // 1分ごとにチェック（失効5分前で更新するので 401 はほぼ発生しない）
  setInterval(() => void maybeRefresh(), 60 * 1000);

  // タブ復帰・フォーカス時にもチェック
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void maybeRefresh();
  });
  window.addEventListener("focus", () => void maybeRefresh());
}
